import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Virtual } from 'swiper/modules';
import 'swiper/css/virtual';
import type { Swiper as SwiperType } from 'swiper';
// Swiper global stylesheets. Ideally these are imported once at the app
// entrypoint rather than per-component, to avoid duplicate stylesheet
// injection when multiple Swiper consumers exist. Tracked for cleanup
// in a follow-up: move to app entrypoint (mi-common-renderer or renderer _app.tsx).
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import clsx from 'clsx';
import { baseVariables } from '../../styles';

import { MMediaSelector } from '../../molecules/MMediaSelector/index';
import { MMediaSelectorMediaType } from '../../molecules/MMediaSelector/MMediaSelector.types';
import type { MMediaSelectorProps } from '../../molecules/MMediaSelector/MMediaSelector.types';
import { MButtonOverlay } from '../../molecules/MButtonOverlay/index';
import { MVideoBackgroundButtonPosition } from '../../molecules/MVideoBackground/MVideoBackground.types';
import { AScrimGradient, AScrimGradientDirection } from '../../atoms/AScrimGradient/index';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import { useDirection } from '../../hooks';
import * as Styled from './OCarouselGalleryHero.styles';
import {
  OCarouselGalleryHeroProps,
  OCarouselGalleryHeroImageScale,
  OCarouselGalleryHeroCarouselType,
} from './OCarouselGalleryHero.types';
import { MCarouselControls } from '../../molecules/MCarouselControls';
import { MCarouselControlsType } from '../../molecules/MCarouselControls/MCarouselControls.types';
import { getRenditionSpecsForScale, getVideoAspectRatios } from './OCarouselGalleryHero.utils';
import {
  DEFAULT_CAROUSEL_LABEL,
  DEFAULT_SLIDE_LABEL,
  DEFAULT_GALLERY_BUTTON_TEXT,
  DEFAULT_VIRTUAL_TOUR_BUTTON_TEXT,
} from '../../utils/i18nConstants';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OCarouselGalleryHero';
const TEST_ID = 'mi-web-ui-kit-OCarouselGalleryHero';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const DESKTOP_MEDIA_QUERY = baseVariables.mediaQuery.md.trim();
const DOTS_OVERFLOW_THRESHOLD = 5;

const getCarouselControlsType = (type?: OCarouselGalleryHeroCarouselType): MCarouselControlsType =>
  type === OCarouselGalleryHeroCarouselType.Numbers ? MCarouselControlsType.Numbers : MCarouselControlsType.Dots;

const getImageLoading = (slideIndex: number, activeIndex: number): 'eager' | 'lazy' =>
  Math.abs(slideIndex - activeIndex) <= 1 ? 'eager' : 'lazy';

/**
 * Reads a media query match synchronously (SSR-safe).
 * Used to seed the initial desktop state so the first render of an autoplay
 * background video already knows it should play. The shared `useMediaQuery`
 * hook reports `false` on its first render and only corrects in an effect,
 * which is too late for the Brightcove player: its autoplay option is frozen
 * at mount, so a late "desktop" signal leaves the player created with
 * autoplay disabled and playback never starts (observed on Windows across
 * Chromium browsers such as Chrome and Edge, where a programmatic play() issued
 * before the player is ready silently no-ops).
 */
const matchesMediaQuery = (query: string): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false;

const resolveCarouselType = (mCarouselControls?: {
  type?: string;
  imageOverlay?: string | boolean;
}): OCarouselGalleryHeroCarouselType => {
  const rawType = mCarouselControls?.type;
  if (rawType === 'Numbers' || rawType === 'Digits') return OCarouselGalleryHeroCarouselType.Numbers;
  return OCarouselGalleryHeroCarouselType.Dots;
};

interface EnhanceMediaContext {
  index: number;
  currentSlide: number;
  isActiveSlide: boolean;
  renditionSpecs: ReturnType<typeof getRenditionSpecsForScale>;
  prefersReducedMotion: boolean;
  isDesktop: boolean;
}

function enhanceMediaItem(item: MMediaSelectorProps, ctx: EnhanceMediaContext): MMediaSelectorProps {
  const isImage = item.mediaType === MMediaSelectorMediaType.Image || !item.mediaType;

  if (isImage && item.mMediaImage) {
    return {
      ...item,
      mMediaImage: {
        ...item.mMediaImage,
        renditionSpecs: ctx.renditionSpecs,
        loading: getImageLoading(ctx.index, ctx.currentSlide),
        aImage: item.mMediaImage.aImage
          ? { ...item.mMediaImage.aImage, fetchpriority: ctx.index === 0 ? 'high' : 'auto' }
          : undefined,
      },
    };
  }

  const isAutoplayVideo =
    item.mediaType === MMediaSelectorMediaType.BackgroundVideo ||
    item.mediaType === MMediaSelectorMediaType.AutoplayVideo;

  if (isAutoplayVideo && item.mVideoBackground) {
    return {
      ...item,
      mVideoBackground: {
        ...item.mVideoBackground,
        fillContainer: true,
        autoPlay: ctx.isActiveSlide && !ctx.prefersReducedMotion && ctx.isDesktop,
        buttonPosition: MVideoBackgroundButtonPosition.Top,
        aVideo: { ...item.mVideoBackground.aVideo, isPlaying: ctx.isActiveSlide },
      },
    };
  }

  if (item.mediaType === MMediaSelectorMediaType.ModalVideo && item.mVideoThumbnail) {
    return {
      ...item,
      mVideoThumbnail: {
        ...item.mVideoThumbnail,
        renditionSpecs: ctx.renditionSpecs,
        loading: getImageLoading(ctx.index, ctx.currentSlide),
        aImage: item.mVideoThumbnail.aImage
          ? { ...item.mVideoThumbnail.aImage, fetchpriority: ctx.index === 0 ? 'high' : 'auto' }
          : undefined,
      },
    };
  }

  return item;
}

export const OCarouselGalleryHero: FC<OCarouselGalleryHeroProps> = ({
  imageScale = OCarouselGalleryHeroImageScale.Large,
  loop = false,
  mCarouselControls,
  galleryButton,
  virtualTourButton,
  mMediaSelectorMulti = [],
  mButtonOverlayGallery,
  mButtonOverlayVirtualTour,
  clickTrackingValues,
  trackingProperties: _trackingProperties,
  className,
  virtualThreshold = 10,
  ...nativeProps
}) => {
  const showGallery = galleryButton === 'true' || galleryButton === true;
  const showVirtualTour = virtualTourButton === 'true' || virtualTourButton === true;
  const resolvedCarouselType = resolveCarouselType(mCarouselControls);
  const effectiveCarouselType =
    resolvedCarouselType === OCarouselGalleryHeroCarouselType.Dots &&
    mMediaSelectorMulti.length > DOTS_OVERFLOW_THRESHOLD
      ? OCarouselGalleryHeroCarouselType.Numbers
      : resolvedCarouselType;
  const showScrim = effectiveCarouselType === OCarouselGalleryHeroCarouselType.Numbers;
  const { 'aria-label': ariaLabel, ...restNativeProps } = nativeProps;
  const swiperRef = useRef<SwiperType | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(mMediaSelectorMulti?.length || 0);
  // Desktop state seeded synchronously (SSR-safe) and kept in sync via a listener.
  // Unlike the shared `useMediaQuery`, this is correct on the very first render,
  // which matters because the Brightcove player freezes its autoplay option at
  // mount — a late desktop signal would leave autoplay disabled and the video
  // would never start (observed on Windows across Chromium browsers like Chrome
  // and Edge).
  const [isDesktop, setIsDesktop] = useState(() => matchesMediaQuery(DESKTOP_MEDIA_QUERY));
  // PRM state seeded synchronously for the same reason as isDesktop: the autoPlay
  // prop passed to MVideoBackground is frozen at mount by the Brightcove player.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => matchesMediaQuery(REDUCED_MOTION_QUERY));
  const { direction } = useDirection();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    setIsDesktop(mediaQuery.matches);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handleChange);
    setPrefersReducedMotion(mq.matches);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  const handleSlideChange = (swiper: SwiperType) => {
    setCurrentSlide(swiper.realIndex ?? swiper.activeIndex);
  };

  const handleNext = () => {
    swiperRef.current?.slideNext();
  };

  const handlePrev = () => {
    swiperRef.current?.slidePrev();
  };

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight' && swiperRef.current) {
      event.preventDefault();
      swiperRef.current.slideNext();
    } else if (event.key === 'ArrowLeft' && swiperRef.current) {
      event.preventDefault();
      swiperRef.current.slidePrev();
    }
  }, []);

  const renditionSpecs = getRenditionSpecsForScale(imageScale);
  const videoRatios = getVideoAspectRatios(imageScale);

  const isVirtual = mMediaSelectorMulti.length >= virtualThreshold;
  const modules = isVirtual ? [Navigation, Pagination, A11y, Virtual] : [Navigation, Pagination, A11y];

  return (
    <Styled.Wrapper
      {...restNativeProps}
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel || DEFAULT_CAROUSEL_LABEL}
      onKeyDown={handleKeyDown}
      className={clsx('o-carousel-gallery-hero', className, 'inverse')}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
    >
      <Styled.CarouselContainer>
        <Swiper
          key={direction}
          dir={direction}
          modules={modules}
          spaceBetween={0}
          slidesPerView={1}
          virtual={isVirtual ? { addSlidesBefore: 2, addSlidesAfter: 2 } : undefined}
          onSwiper={(swiper: SwiperType) => {
            swiperRef.current = swiper;
            setTotalSlides(isVirtual ? mMediaSelectorMulti.length : swiper.slides.length);
          }}
          onSlideChange={handleSlideChange}
          loop={loop}
          className="carousel-swiper"
        >
          {mMediaSelectorMulti?.map((mediaItem, index) => {
            const isActiveSlide = index === currentSlide;
            const enhancedItem = enhanceMediaItem(mediaItem, {
              index,
              currentSlide,
              isActiveSlide,
              renditionSpecs,
              prefersReducedMotion,
              isDesktop,
            });

            return (
              <SwiperSlide key={index} virtualIndex={isVirtual ? index : undefined}>
                <Styled.SlideContent
                  role="group"
                  aria-roledescription="slide"
                  aria-label={DEFAULT_SLIDE_LABEL.replace('{x}', String(index + 1)).replace('{y}', String(totalSlides))}
                  aria-hidden={!isActiveSlide || undefined}
                  inert={!isActiveSlide ? '' : undefined}
                >
                  <Styled.MediaSlideWrapper
                    $mobileRatio={videoRatios.mobile}
                    $desktopRatio={videoRatios.desktop}
                    data-testid="media-slide-wrapper"
                  >
                    <MMediaSelector {...enhancedItem} />
                  </Styled.MediaSlideWrapper>
                </Styled.SlideContent>
              </SwiperSlide>
            );
          })}
        </Swiper>

        <Styled.ControlsContainer>
          {showScrim && (
            <Styled.ScrimOverlay>
              <AScrimGradient direction={AScrimGradientDirection.BottomToTop} />
            </Styled.ScrimOverlay>
          )}

          <Styled.CarouselWithButtons data-testid="carousel-with-buttons">
            {mMediaSelectorMulti.length > 1 && (
              <MCarouselControls
                type={getCarouselControlsType(effectiveCarouselType)}
                totalSlides={totalSlides}
                currentSlide={currentSlide}
                onNext={handleNext}
                onPrev={handlePrev}
                imageOverlay={effectiveCarouselType === OCarouselGalleryHeroCarouselType.Dots}
                loop={loop}
                previousClickTrackValue={clickTrackingValues?.carouselBack}
                nextClickTrackValue={clickTrackingValues?.carouselForward}
              />
            )}

            {showGallery && (
              <MButtonOverlay
                iconOnly={!isDesktop}
                aria-label={!isDesktop ? DEFAULT_GALLERY_BUTTON_TEXT : undefined}
                aIcon={{ icon: 'icon-photo-gallery', size: AIconSize.L }}
                text={DEFAULT_GALLERY_BUTTON_TEXT}
                {...mButtonOverlayGallery}
                imageOverlay={!showScrim}
                data-custom_click_track_value={clickTrackingValues?.gallery}
              />
            )}
            {showVirtualTour && (
              <MButtonOverlay
                iconOnly={!isDesktop}
                aria-label={!isDesktop ? DEFAULT_VIRTUAL_TOUR_BUTTON_TEXT : undefined}
                aIcon={{ icon: 'icon-3d-box', size: AIconSize.L }}
                text={DEFAULT_VIRTUAL_TOUR_BUTTON_TEXT}
                {...mButtonOverlayVirtualTour}
                imageOverlay={!showScrim}
                data-custom_click_track_value={clickTrackingValues?.virtualTour}
              />
            )}
          </Styled.CarouselWithButtons>
        </Styled.ControlsContainer>
      </Styled.CarouselContainer>

      <Styled.VisuallyHidden aria-live="polite" aria-atomic="true">
        {DEFAULT_SLIDE_LABEL.replace('{x}', String(currentSlide + 1)).replace('{y}', String(totalSlides))}
      </Styled.VisuallyHidden>
    </Styled.Wrapper>
  );
};

export * from './OCarouselGalleryHero.types';
